import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { GitCompareArrows, Plus } from "lucide-react";
import Sidebar from "@/components/sidebar";
import MobileMenuButton from "@/components/ui/mobile-menu-button";
import PageHeader from "@/components/page-header.jsx";
import PageHeaderActions from "@/components/page-header-actions.jsx";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext";
import { useFilters } from "@/hooks/useFilters";
import { useTagDataContext } from "@/contexts/TagDataContext";
import { useUniversalDescriptorsContext } from "@/contexts/UniversalDescriptorsContext.jsx";
import { getActiveDescriptorsForOperatingPointsPicker } from "@/lib/universal-descriptor-operating-points-utils.js";
import { useOperatingPointsParameterMap } from "@/hooks/useOperatingPointsParameterMap";
import { useOperatingPointsTagDataFetching } from "@/hooks/useOperatingPointsTagDataFetching";
import { useOperatingPointsViews } from "@/hooks/useOperatingPointsViews";
import { useOperatingPointsSaveHandlers } from "@/hooks/useOperatingPointsSaveHandlers";
import { useOperatingPointsNavigationGuard } from "@/hooks/useOperatingPointsNavigationGuard";
import {
  DEFAULT_TABS,
  ASSET_PROPERTY_NAMES,
  DEFAULT_COLUMNS,
  MEASUREMENT_COLUMNS,
} from "@/constants/operating-points-constants";
import { removeCommasAndSpaces } from "@/lib/formatting-utils";
import { Button } from "@/components/ui/button";
import FilterDrawer from "@/components/filter-drawer";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import CreateViewDialog from "@/components/operating-points/create-view-dialog";
import DeleteViewDialog from "@/components/operating-points/delete-view-dialog";
import ColumnDialog from "@/components/operating-points/column-dialog";
import UnsavedChangesDialog from "@/components/operating-points/unsaved-changes-dialog";
import { cardStyles, outlineIconButton } from "@/lib/status-styles";
import { cn, mathUtils } from "@/lib/utils";
import {
  parseTableStateFromSearchString,
  buildTableStateSearchParams,
  appendTableStateToSearch,
} from "@/lib/table-url-state.js";
import LoadingIndicator from "@/components/ui/loading-indicator";
import { LoadingSkeleton } from "@/components/ui/loading";
import OperatingPointsTabs from "@/components/operating-points/operating-points-tabs";
import OperatingPointsTable from "@/components/operating-points/operating-points-table";

/**
 * Operating Points Comparison page component
 * Uses the same asset data from fleet overview with 15-minute auto-refresh
 * @returns {JSX.Element} Operating Points page
 */
export default function OperatingPoints() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Use the same asset data from AssetContext (same as fleet overview)
  const { assets, isLoading } = useAssets();

  // Use the same filters hook as fleet overview
  const { filters, handleFilterChange, handleStatusClick, clearFilters } =
    useFilters();

  // Get tag data context to fetch tag data for all assets
  const { getTagData } = useTagDataContext();

  const { universalDescriptors, isLoading: isUniversalDescriptorsLoading } =
    useUniversalDescriptorsContext();

  const descriptorPickerItems = useMemo(
    () => getActiveDescriptorsForOperatingPointsPicker(universalDescriptors),
    [universalDescriptors]
  );

  const {
    getUniversalDescriptorParameterMap,
    parameterMapsRef,
  } = useOperatingPointsParameterMap();

  const findUnitForUniversalDescriptorId = useCallback(
    (udId) => {
      if (!Number.isFinite(udId)) return "";
      /** @type {Map<string, number>} */
      // Since one bogus-ish unit ('TEST ONLY' when I found this potential problem) can make
      //   the whole column funky ... count units and pick the most frequently found unit.
      //   Enbase's units are freeform text so don't trust the units completely.
      const unitVotes = new Map();
      for (const asset of assets || []) {
        if (!asset?.id || typeof asset.id !== "string") continue;
        const assetId = asset.id.trim();
        const tagDataEntry = getTagData(assetId);
        if (!tagDataEntry?.tagData) continue;
        const udMap = getUniversalDescriptorParameterMap(
          assetId,
          tagDataEntry.tagData
        );
        const p = udMap?.get(udId);
        const u = (p?.unit && String(p.unit).trim()) || "";
        if (!u) continue;
        unitVotes.set(u, (unitVotes.get(u) || 0) + 1);
      }
      let best = "";
      let bestCount = 0;
      for (const [u, n] of unitVotes) {
        if (n > bestCount || (n === bestCount && u.localeCompare(best) < 0)) {
          best = u;
          bestCount = n;
        }
      }
      return best;
    },
    [assets, getTagData, getUniversalDescriptorParameterMap]
  );

  const getColumnUnit = useCallback(
    ({ universalDescriptorId }) => {
      const udId =
        universalDescriptorId != null ? Number(universalDescriptorId) : NaN;
      return findUnitForUniversalDescriptorId(udId);
    },
    [findUnitForUniversalDescriptorId]
  );

  // Views and columns management (includes handlers)
  const {
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    customColumns,
    setCustomColumns,
    savedColumns,
    setSavedColumns,
    replacedDefaultTabs,
    setReplacedDefaultTabs,
    viewNameToBackendId,
    setViewNameToBackendId,
    isLoadingViews,
    isSaving,
    isDeleting,
    isCreating,
    setIsSaving,
    setIsDeleting,
    setIsCreating,
    handleRestoreDefaultViews,
    extractViewId,
    viewsCacheRef,
    handleCreateView,
    handleDeleteView,
    handleAddColumn,
    handleUpdateColumn: handleUpdateColumnBase,
    handleDeleteColumn: handleDeleteColumnBase,
    handleMoveColumnLeft,
    handleMoveColumnRight,
    mergeMeasurementColumns,
    applyOperatingPointsViewsFromApiResponse,
  } = useOperatingPointsViews(getColumnUnit);

  // Save/discard handlers and unsaved changes detection
  const {
    handleSave,
    handleSaveAll,
    handleDiscardAll,
    checkTabHasUnsavedChanges,
    hasUnsavedChanges,
    getUnsavedTabs,
    unsavedViewNames,
    hasAnyUnsavedChanges,
  } = useOperatingPointsSaveHandlers({
    tabs,
    activeTab,
    customColumns,
    setCustomColumns,
    savedColumns,
    setReplacedDefaultTabs,
    viewNameToBackendId,
    setIsSaving,
    mergeMeasurementColumns,
    applyOperatingPointsViewsFromApiResponse,
  });

  // Navigation guard with unsaved changes protection
  const {
    navigateWithUnsavedCheck,
    handleNavigationAfterAction,
    isUnsavedChangesDialogOpen,
    setIsUnsavedChangesDialogOpen,
    pendingNavigation,
    setPendingNavigation,
  } = useOperatingPointsNavigationGuard({
    hasAnyUnsavedChanges,
  });

  const locationSearch = useSearch();
  const [, setLocation] = useLocation();
  const fromUrl = parseTableStateFromSearchString(locationSearch, undefined);

  const [sortColumn, setSortColumn] = useState(fromUrl.sortColumn);
  const [sortDirection, setSortDirection] = useState(fromUrl.sortDirection);
  const [currentPage, setCurrentPage] = useState(fromUrl.page);
  const userHasChangedPaginationRef = useRef(false);
  const pendingTabRestoreRef = useRef(false);
  const prevLocationSearchRef = useRef("");
  const [isCreateViewModalOpen, setIsCreateViewModalOpen] = useState(false);
  const [isDeleteViewModalOpen, setIsDeleteViewModalOpen] = useState(false);
  const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);
  const [isEditColumnModalOpen, setIsEditColumnModalOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);

  /** @type {[number, Function]} */
  const [itemsPerPage, setItemsPerPage] = useState(fromUrl.itemsPerPage);

  const getSearchParams = (search) => {
    const s =
      typeof search === "string" && search.startsWith("?")
        ? search.slice(1)
        : search || "";
    return new URLSearchParams(s);
  };

  // Sync page/sort from URL when it changes (e.g. back from asset overview)
  useEffect(() => {
    const parsed = parseTableStateFromSearchString(locationSearch, undefined);
    setCurrentPage(parsed.page);
    setItemsPerPage(parsed.itemsPerPage);
    setSortColumn(parsed.sortColumn);
    setSortDirection(parsed.sortDirection);
  }, [locationSearch]);

  // Set "restore tab from URL" flag only when locationSearch changes (e.g. navigated back). No activeTab in deps
  // so the hook's initial setActiveTab(first) doesn't re-run this and clear the flag before we restore.
  useEffect(() => {
    if (locationSearch === prevLocationSearchRef.current) return;
    prevLocationSearchRef.current = locationSearch;
    const params = getSearchParams(locationSearch);
    const tabInUrl = params.get("tab");
    pendingTabRestoreRef.current = !!(tabInUrl && tabInUrl !== activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: omit activeTab so restore flag survives hook setActiveTab
  }, [locationSearch]);

  // Apply tab from URL once tabs are loaded (e.g. back from asset overview)
  useEffect(() => {
    if (tabs.length === 0) return;
    const params = getSearchParams(locationSearch);
    const tabFromUrl = params.get("tab");
    if (tabFromUrl && tabs.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [locationSearch, tabs, setActiveTab]);

  // Keep URL in sync with page, sort, and active tab; restore tab from URL when needed
  useEffect(() => {
    const urlSearch = window.location.search;
    const currentParams = getSearchParams(urlSearch);
    const tabFromUrl = currentParams.get("tab");
    const urlTabValid = tabFromUrl && tabs.some((t) => t.id === tabFromUrl);

    if (urlTabValid && tabFromUrl === activeTab) {
      pendingTabRestoreRef.current = false;
    }

    const searchParams = buildTableStateSearchParams({
      page: currentPage,
      itemsPerPage,
      sortColumn,
      sortDirection,
    });
    if (activeTab) searchParams.set("tab", activeTab);

    const query = searchParams.toString();
    const target = query ? `/operating-points?${query}` : "/operating-points";
    const current = window.location.pathname + (window.location.search || "");
    if (current === target) return;

    if (currentParams.has("tab") && tabs.length === 0) return;

    if (
      pendingTabRestoreRef.current
      && urlTabValid
      && tabFromUrl !== activeTab
    ) {
      setActiveTab(tabFromUrl);
      pendingTabRestoreRef.current = false;
      return;
    }

    const currentHasParams = urlSearch.length > 0;
    const currentUrlHasPage = currentParams.has("page");
    const targetWouldDropPage = currentPage === 1;
    const urlTabMatchesState = !tabFromUrl || tabFromUrl === activeTab;
    const targetSortMatchesUrl =
      (searchParams.get("sort") || null) === (currentParams.get("sort") || null)
      && (searchParams.get("dir") || "asc")
        === (currentParams.get("dir") || "asc");
    const shouldPreferUrl =
      !userHasChangedPaginationRef.current
      && currentHasParams
      && urlTabMatchesState
      && targetSortMatchesUrl
      && (target === "/operating-points"
        || (currentUrlHasPage && targetWouldDropPage));

    if (shouldPreferUrl) {
      const parsed = parseTableStateFromSearchString(urlSearch, undefined);
      setCurrentPage(parsed.page);
      setItemsPerPage(parsed.itemsPerPage);
      setSortColumn(parsed.sortColumn);
      setSortDirection(parsed.sortDirection);
      if (urlTabValid) setActiveTab(tabFromUrl);
      return;
    }

    setLocation(target, { replace: true });
  }, [
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    activeTab,
    tabs,
    setLocation,
    setActiveTab,
  ]);

  // Get filtered assets based on filters and search
  // useFilteredAssets already handles search filtering internally
  const { filteredAssets } = useFilteredAssets(filters, search);

  const descriptorAssetCounts = useMemo(() => {
    const counts = new Map();
    const idSet = new Set(
      descriptorPickerItems.map((d) => d.universalDescriptorId)
    );
    for (const asset of filteredAssets || []) {
      if (!asset?.id || typeof asset.id !== "string") continue;
      const assetId = asset.id.trim();
      const tagDataEntry = getTagData(assetId);
      if (!tagDataEntry?.tagData) continue;
      const udMap = getUniversalDescriptorParameterMap(
        assetId,
        tagDataEntry.tagData
      );
      if (!udMap?.size) continue;
      udMap.forEach((_, id) => {
        if (idSet.has(id)) {
          counts.set(id, (counts.get(id) || 0) + 1);
        }
      });
    }
    return counts;
  }, [
    filteredAssets,
    getTagData,
    getUniversalDescriptorParameterMap,
    descriptorPickerItems,
  ]);

  // Tag data fetching management
  const { fetchingAssetIds } = useOperatingPointsTagDataFetching(
    assets,
    filteredAssets,
    activeTab,
    customColumns,
    ASSET_PROPERTY_NAMES,
    MEASUREMENT_COLUMNS,
    getUniversalDescriptorParameterMap,
    parameterMapsRef
  );

  // Get measurement columns for active tab
  // Custom tabs (starting with "custom-") will have empty columns by default
  // Merge default columns with custom columns for the active tab
  // Custom columns override default columns with the same key
  const measurementColumns = useMemo(() => {
    if (!activeTab) return [];

    const defaultColumns = activeTab.startsWith("custom-")
      ? []
      : MEASUREMENT_COLUMNS[activeTab] || [];
    const rawCustom = customColumns[activeTab] || [];
    // Only show columns that belong to this tab (strict: _tabId must match so we never show another tab's columns)
    const custom = rawCustom.filter((col) => col._tabId === activeTab);

    // If custom contains all default columns (meaning order was changed), use custom order
    if (!activeTab.startsWith("custom-") && defaultColumns.length > 0) {
      const defaultKeys = new Set(defaultColumns.map((col) => col.key));
      const customKeys = new Set(custom.map((col) => col.key));
      const hasAllDefaults = defaultColumns.every((col) =>
        customKeys.has(col.key)
      );

      if (hasAllDefaults && custom.length >= defaultColumns.length) {
        const fromCustom = custom.filter(
          (col) => !col.tagName || !ASSET_PROPERTY_NAMES.has(col.tagName)
        );
        return fromCustom.filter(
          (col) =>
            col.universalDescriptorId != null
            && Number.isFinite(Number(col.universalDescriptorId))
        );
      }
    }

    // Normal merge: default columns with custom modifications
    // Create a map of custom columns by key for override lookup
    const customMap = new Map(custom.map((col) => [col.key, col]));

    // Start with default columns, but replace with custom if key exists
    const merged = defaultColumns.map((col) => {
      return customMap.has(col.key) ? customMap.get(col.key) : col;
    });

    // Add custom columns that don't override defaults
    custom.forEach((col) => {
      if (!defaultColumns.some((dc) => dc.key === col.key)) {
        merged.push(col);
      }
    });

    // Filter out default asset property columns (they're always shown as fixed columns)
    const withoutAssetCols = merged.filter(
      (col) => !col.tagName || !ASSET_PROPERTY_NAMES.has(col.tagName)
    );
    return withoutAssetCols.filter(
      (col) =>
        col.universalDescriptorId != null
        && Number.isFinite(Number(col.universalDescriptorId))
    );
  }, [activeTab, customColumns]);

  /**
   * Merge live units from tag data (per UD id) with stored column.unit. Saved/custom state
   * often omits or clears unit when it overrides defaults; values still resolve via UD id.
   */
  const measurementColumnsWithUnits = useMemo(() => {
    return measurementColumns.map((col) => {
      const udRaw = col.universalDescriptorId;
      const udId = udRaw != null ? Number(udRaw) : NaN;
      const fromTagData =
        Number.isFinite(udId)
          ? getColumnUnit({ universalDescriptorId: udId })
          : "";
      const trimmedStored = col.unit && String(col.unit).trim();
      const unit =
        (fromTagData && String(fromTagData).trim())
        || trimmedStored
        || "";
      return { ...col, unit };
    });
  }, [measurementColumns, getColumnUnit]);

  const columnDialogExistingColumns = useMemo(
    () => [...DEFAULT_COLUMNS, ...measurementColumnsWithUnits],
    [measurementColumnsWithUnits]
  );

  /**
   * Helper function to get tag value for an asset and column
   * Optimized to use parameter map for O(1) lookups instead of O(n) search
   * @param {Object} asset - The asset object
   * @param {Object} column - The column definition with tagName property
   * @returns {number|string|null} The value or null if not found
   */
  const formatParameterValue = (foundParameter) => {
    if (
      !foundParameter
      || foundParameter.currentValue === undefined
      || foundParameter.currentValue === null
    ) {
      return null;
    }
    const currentValue = String(foundParameter.currentValue).trim();
    if (foundParameter.displayType === "Text") {
      return currentValue || null;
    }
    const numValue =
      typeof currentValue === "string"
        ? parseFloat(removeCommasAndSpaces(currentValue))
        : Number(currentValue);
    if (
      typeof numValue !== "number"
      || isNaN(numValue)
      || !isFinite(numValue)
    ) {
      return 0;
    }
    return numValue;
  };

  const getTagValueForAsset = useCallback(
    (asset, column) => {
      if (!asset || !column) {
        return null;
      }

      const tagName = column.tagName;
      const udIdRaw = column.universalDescriptorId;
      const udId = udIdRaw != null ? Number(udIdRaw) : NaN;

      if (tagName && ASSET_PROPERTY_NAMES.has(tagName)) {
        if (tagName === "assetNumber") {
          return asset.assetNumber || null;
        } else if (tagName === "lease") {
          return asset.lease || null;
        } else if (tagName === "status") {
          return asset.status || null;
        } else if (tagName === "lastUpdated") {
          return asset.lastUpdated || null;
        }
        return null;
      }

      if (!asset?.id || typeof asset.id !== "string") {
        return null;
      }

      const assetId = asset.id.trim();
      const tagDataEntry = getTagData(assetId);
      if (!tagDataEntry?.tagData) {
        return null;
      }

      if (!Number.isFinite(udId)) {
        return null;
      }
      const udMap = getUniversalDescriptorParameterMap(
        assetId,
        tagDataEntry.tagData
      );
      return formatParameterValue(udMap?.get(udId) ?? null);
    },
    [getTagData, getUniversalDescriptorParameterMap]
  );

  // Wrapper for handleUpdateColumn that closes modal
  const handleUpdateColumn = (payload) => {
    handleUpdateColumnBase(payload, editingColumn);
    setIsEditColumnModalOpen(false);
    setEditingColumn(null);
  };

  // Wrapper for handleDeleteColumn that closes modal
  const handleDeleteColumn = () => {
    handleDeleteColumnBase(editingColumn);
    setIsEditColumnModalOpen(false);
    setEditingColumn(null);
  };

  // Get the current active tab name for the delete dialog
  const activeTabName = tabs.find((tab) => tab.id === activeTab)?.label || "";

  // Check if the active tab can be deleted (all tabs can be deleted now)
  const canDeleteTab = useMemo(() => {
    if (!activeTab) return false;
    // Find the current tab in the tabs array
    const currentTab = tabs.find((tab) => tab.id === activeTab);
    if (!currentTab) return false;
    // Allow deletion of all tabs (default and custom)
    // Only prevent deletion if there are no tabs left (handled in handleDeleteView)
    return true;
  }, [activeTab, tabs]);

  // Handle opening edit column dialog
  const handleEditColumn = (column) => {
    setEditingColumn(column);
    setIsEditColumnModalOpen(true);
  };

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Sort assets - updated to handle lease sorting and measurement columns
  const sortedAssets = useMemo(() => {
    if (!filteredAssets || !sortColumn) return filteredAssets || [];

    // Find the column definition for measurement columns
    const columnDef = measurementColumns.find((col) => col.key === sortColumn);

    const sorted = [...filteredAssets].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle different data types
      if (sortColumn === "assetNumber") {
        aVal = a.assetNumber || "";
        bVal = b.assetNumber || "";
      } else if (sortColumn === "lease") {
        aVal = a.lease || "";
        bVal = b.lease || "";
      } else if (sortColumn === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
      } else if (sortColumn === "lastUpdated") {
        aVal = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
        bVal = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
      } else if (columnDef) {
        // Handle measurement columns - get tag values from tag data
        aVal = getTagValueForAsset(a, columnDef);
        bVal = getTagValueForAsset(b, columnDef);

        // Handle null values - put them at the end
        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return 1;
        if (bVal === null) return -1;
      } else {
        // Fallback for unknown columns
        aVal =
          a[sortColumn] !== null && a[sortColumn] !== undefined
            ? parseFloat(a[sortColumn]) || 0
            : null;
        bVal =
          b[sortColumn] !== null && b[sortColumn] !== undefined
            ? parseFloat(b[sortColumn]) || 0
            : null;

        // Handle null values - put them at the end
        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return 1;
        if (bVal === null) return -1;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [
    filteredAssets,
    sortColumn,
    sortDirection,
    measurementColumns,
    getTagValueForAsset,
  ]);

  // Pagination calculations
  const totalPages = mathUtils.ceil((sortedAssets?.length || 0) / itemsPerPage);

  // Reset to page 1 if current page exceeds total pages (e.g., after filtering)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    userHasChangedPaginationRef.current = true;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    userHasChangedPaginationRef.current = true;
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const adjustedCurrentPage = currentPage > totalPages ? 1 : currentPage;
  const startIndex = (adjustedCurrentPage - 1) * itemsPerPage;
  const paginatedAssets = (sortedAssets || []).slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <div className='flex h-screen bg-background relative'>
      {/* Mobile menu button */}
      <MobileMenuButton
        isOpen={isMobileMenuOpen}
        onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Sidebar */}
      <Sidebar
        key='persistent-sidebar'
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      {/* Main Content - min-h-0 so flex child can shrink and show scrollbar */}
      <div className='flex-1 flex flex-col min-h-0 overflow-hidden transition-all duration-200 ease-in-out'>
        {/* Header */}
        <PageHeader
          icon={GitCompareArrows}
          title='Operating Points Comparison'
          subtitle={`Compare measurement values across ${filteredAssets?.length || 0} assets`}>
          <PageHeaderActions showLastUpdated />
        </PageHeader>

        {/* Main Content — filters shrink; table fills with a scrollable floor */}
        <div className={cardStyles.pageContent}>
          <div className='flex flex-col flex-1 min-h-full gap-6'>
            <div className='shrink-0'>
              <FilterDrawer
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                onStatusClick={handleStatusClick}
                searchTerm={search}
              />
            </div>

            {/* Tabs and Table Card */}
            <Card
              className={cn(
                "bg-card border border-border shadow-sm rounded-lg overflow-hidden relative",
                cardStyles.tableFillRegion,
                "min-h-[min(85vh,52rem)]"
              )}>
              {/* Loading overlay for delete/create operations */}
              {(isDeleting || isCreating) && (
                <div className='absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-t-lg'>
                  <LoadingIndicator
                    size='lg'
                    text={isDeleting ? "Deleting view..." : "Creating view..."}
                  />
                </div>
              )}
              {isLoadingViews ? (
                <>
                  {/* Skeleton: Tabs and Action Buttons */}
                  <div className='shrink-0 flex items-center justify-between px-6 py-4 border-b border-border bg-card rounded-t-lg gap-4'>
                    <nav className='bg-muted rounded-lg px-4 py-2 flex-1 min-w-0 overflow-hidden'>
                      <div className='flex items-center justify-start gap-2 overflow-x-auto'>
                        {[...Array(3)].map((_, i) => (
                          <LoadingSkeleton
                            key={i}
                            className='h-9 w-32 rounded-md'
                          />
                        ))}
                      </div>
                    </nav>
                    <div className='flex items-center gap-2'>
                      <LoadingSkeleton className='h-8 w-8 rounded' />
                      <LoadingSkeleton className='h-8 w-8 rounded' />
                    </div>
                  </div>

                  {/* Skeleton: Table Header */}
                  <CardHeader className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 border-b border-border'>
                    <LoadingSkeleton className='h-6 w-40 rounded' />
                    <div className='flex items-center gap-4'>
                      <LoadingSkeleton className='h-9 w-20 rounded' />
                      <LoadingSkeleton className='h-9 w-24 rounded' />
                      <LoadingSkeleton className='h-9 w-48 rounded' />
                    </div>
                  </CardHeader>

                  {/* Skeleton: Table */}
                  <CardContent
                    className={cn(
                      cardStyles.content,
                      "flex flex-col flex-1 min-h-0"
                    )}>
                    <div
                      className={cn(
                        cardStyles.tableContainer,
                        cardStyles.tableFillBody
                      )}>
                      <Table className='w-full'>
                        <TableHeader className={cardStyles.tableHeader}>
                          <TableRow className={cardStyles.tableRow}>
                            {[...Array(7)].map((_, i) => (
                              <TableHead
                                key={i}
                                className={cn(cardStyles.tableHead, "w-24")}>
                                <LoadingSkeleton className='h-5 w-20 rounded' />
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[...Array(5)].map((_, rowIndex) => (
                            <TableRow
                              key={rowIndex}
                              className={cardStyles.bodyRow}>
                              {[...Array(7)].map((_, colIndex) => (
                                <TableCell
                                  key={colIndex}
                                  className={cardStyles.tableCell}>
                                  <LoadingSkeleton className='h-4 w-16 rounded' />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </>
              ) : tabs.length === 0 || !activeTab ? (
                <>
                  {/* Empty State: No Views */}
                  <div className='shrink-0 flex items-center justify-between px-6 py-4 border-b border-border bg-card rounded-t-lg gap-4'>
                    <nav className='bg-muted rounded-lg px-4 py-2 flex-1 min-w-0 overflow-hidden'>
                      <div className='flex items-center justify-start gap-2'>
                        <span className='text-sm text-muted-foreground italic'>
                          No views available
                        </span>
                      </div>
                    </nav>
                    <div className='flex items-center gap-2'>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => setIsCreateViewModalOpen(true)}
                              disabled={isUniversalDescriptorsLoading}
                              className={cn(
                                outlineIconButton,
                                "h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                              )}>
                              {isUniversalDescriptorsLoading ? (
                                <LoadingIndicator size='sm' />
                              ) : (
                                <Plus className='h-4 w-4' />
                              )}
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {isUniversalDescriptorsLoading
                              ? "Loading..."
                              : "Add view"}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Empty State Message */}
                  <CardContent
                    className={cn(
                      cardStyles.content,
                      "flex-1 flex flex-col min-h-0 items-center justify-center"
                    )}>
                    <div className='text-center max-w-md mx-auto px-4 py-8'>
                      <div className='mb-8'>
                        <svg
                          className='mx-auto h-12 w-12 text-muted-foreground'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                          aria-hidden='true'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                          />
                        </svg>
                      </div>
                      <h3 className='text-lg font-medium text-foreground mb-4'>
                        No views available
                      </h3>
                      <p className='text-sm text-muted-foreground mb-8'>
                        You have deleted all your views. Default views will be
                        restored automatically when you refresh the page or
                        create a new view.
                      </p>
                      <div className='flex flex-col sm:flex-row gap-2 justify-center'>
                        <Button
                          onClick={() => handleRestoreDefaultViews()}
                          className='bg-primary text-white hover:bg-primary/90'>
                          Restore Default Views
                        </Button>
                        <Button
                          variant='outline'
                          onClick={() => setIsCreateViewModalOpen(true)}
                          disabled={isUniversalDescriptorsLoading}
                          className='border-primary text-primary hover:bg-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed'>
                          {isUniversalDescriptorsLoading
                            ? "Loading..."
                            : "Create New View"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </>
              ) : (
                <>
                  {/* Tabs and Action Buttons */}
                  <OperatingPointsTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    canDeleteTab={canDeleteTab}
                    onCreateView={() => setIsCreateViewModalOpen(true)}
                    onDeleteView={() => {
                      if (canDeleteTab) {
                        setIsDeleteViewModalOpen(true);
                      }
                    }}
                    isLoadingTags={isUniversalDescriptorsLoading}
                    checkTabHasUnsavedChanges={checkTabHasUnsavedChanges}
                  />

                  {/* Table */}
                  <OperatingPointsTable
                    data={{
                      measurementColumns: measurementColumnsWithUnits,
                      sortedAssets,
                      paginatedAssets,
                      isLoading,
                    }}
                    sort={{
                      column: sortColumn,
                      direction: sortDirection,
                      onSort: handleSort,
                    }}
                    columnActions={{
                      onEdit: handleEditColumn,
                      onMoveLeft: handleMoveColumnLeft,
                      onMoveRight: handleMoveColumnRight,
                      onAdd: () => setIsAddColumnModalOpen(true),
                    }}
                    tagData={{
                      getValueForAsset: getTagValueForAsset,
                      fetchingAssetIds,
                      getTagData,
                    }}
                    search={{
                      value: search,
                      onChange: setSearch,
                    }}
                    save={{
                      onSave: handleSave,
                      isSaving,
                      hasUnsavedChanges,
                    }}
                    onAssetClick={(asset) => {
                      const base = `/asset/${encodeURIComponent(asset.id)}/overview`;
                      const params = new URLSearchParams();
                      params.set("from", "operating-points");
                      if (activeTab) params.set("tab", activeTab);
                      const tableQuery = appendTableStateToSearch(params, {
                        page: currentPage,
                        itemsPerPage,
                        sortColumn,
                        sortDirection,
                      });
                      navigateWithUnsavedCheck(`${base}?${tableQuery}`);
                    }}
                    pagination={{
                      currentPage: adjustedCurrentPage,
                      totalPages,
                      startIndex,
                      itemsPerPage,
                      totalItems: sortedAssets.length,
                      onPageChange: handlePageChange,
                      onItemsPerPageChange: handleItemsPerPageChange,
                    }}
                  />
                </>
              )}
            </Card>
            <div className={cardStyles.pageBottomSpacer} aria-hidden='true' />
          </div>
        </div>
      </div>

      {/* Create New View Dialog */}
      <CreateViewDialog
        open={isCreateViewModalOpen}
        onOpenChange={setIsCreateViewModalOpen}
        onCreate={handleCreateView}
      />

      {/* Delete View Confirmation Dialog */}
      <DeleteViewDialog
        open={isDeleteViewModalOpen}
        onOpenChange={setIsDeleteViewModalOpen}
        viewName={activeTabName}
        onConfirm={handleDeleteView}
      />

      {/* Add Column Dialog */}
      <ColumnDialog
        open={isAddColumnModalOpen}
        onOpenChange={setIsAddColumnModalOpen}
        descriptorPickerItems={descriptorPickerItems}
        descriptorAssetCounts={descriptorAssetCounts}
        isLoadingDescriptors={isUniversalDescriptorsLoading}
        existingColumns={columnDialogExistingColumns}
        onAdd={handleAddColumn}
      />

      {/* Edit Column Dialog */}
      <ColumnDialog
        open={isEditColumnModalOpen}
        onOpenChange={setIsEditColumnModalOpen}
        descriptorPickerItems={descriptorPickerItems}
        descriptorAssetCounts={descriptorAssetCounts}
        isLoadingDescriptors={isUniversalDescriptorsLoading}
        existingColumns={columnDialogExistingColumns}
        column={editingColumn}
        onUpdate={handleUpdateColumn}
        onDelete={handleDeleteColumn}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={isUnsavedChangesDialogOpen}
        onOpenChange={(open) => {
          setIsUnsavedChangesDialogOpen(open);
          if (!open) {
            setPendingNavigation(null);
          }
        }}
        unsavedViewNames={unsavedViewNames}
        onSaveAll={async () => {
          try {
            await handleSaveAll();
            setIsUnsavedChangesDialogOpen(false);
            handleNavigationAfterAction();
          } catch (error) {
            // Keep dialog open on error
            console.error("Failed to save all views:", error);
          }
        }}
        onDiscardAll={() => {
          handleDiscardAll();
          setIsUnsavedChangesDialogOpen(false);
          handleNavigationAfterAction();
        }}
        onCancel={() => {
          setPendingNavigation(null);
        }}
        isSaving={isSaving}
      />
    </div>
  );
}
